/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useState, useEffect } from "react";
import { AuthPieceProps } from "aws-amplify-react/props";
import { notify, resetUrlPath, getUrlParam, getPlatformUrl } from "./utils";
import { BrowserRouter, Switch, Route, match } from "react-router-dom";
import LandingFooter from "./LandingPage/LandingFooter";
import LandingForm from "./LandingPage/LandingForm";
import { Logout } from "../Home";
import styles from "./LandingPage/LandingForm/LandingForm.module.css";
import TermsPopup from "./TermsPopup";
import { useTranslation } from "react-i18next";

interface LandingPageProps extends AuthPieceProps {
  match?: match<any>;
  staticContext?: any;
  page?: string;
}

export const LandingPage: React.FC<LandingPageProps> = (props) => {
  const { authState, onAuthEvent, onStateChange, page } = props;
  const { changeState } = notify(authState, onAuthEvent, onStateChange);
  const [popupStateClosed, setPopupStateClosed] = useState(false);
  const urlParams = {
    userNamePrefill: getUrlParam("email"),
    userEmail: getUrlParam("userEmail"),
    sessionExpired: getUrlParam("sessionExpired"),
    mode: getUrlParam("mode"),
  };
  const { t } = useTranslation("common");

  // Change state according to path
  useEffect(() => {
    if (process.env.REACT_APP_REDIRECT_TO_PLATFORM) {
      window.location.href = getPlatformUrl();
    }
  }, [page, changeState, authState]);

  // If URL params are not set at this point, make sure no invalid path is set.
  if (
    !urlParams.userNamePrefill &&
    !urlParams.userEmail &&
    !urlParams.sessionExpired &&
    !["/login"].includes(
      window.location.pathname.toLocaleLowerCase()
    )
  ) {
    resetUrlPath();
  }

  // If user is sent from Verimatrix Unified Platform with URL param "t", then
  // don't show free trial info until the browser is restarted.
  if (
    urlParams.mode === "direct" &&
    sessionStorage.getItem("AsNoTrial") !== "true"
  ) {
    sessionStorage.setItem("AsNoTrial", "true");
  }

  // Redirect to Platform login page
  if (process.env.REACT_APP_REDIRECT_TO_PLATFORM) {
    return <div className={styles.landingWrapperLoaded}></div>;
  }

  // Return main landing page
  return (
    <div className={styles.landingWrapperLoaded}>
      <title>{t("title")}</title>
      <LandingForm {...props} urlParams={urlParams} />
      <LandingFooter changeState={changeState} />

      {page === "terms" && !popupStateClosed && (
        <TermsPopup setPopupStateClosed={setPopupStateClosed} />
      )}
    </div>
  );
};

export const ManagementLandingPage: React.FC<LandingPageProps> = (props) => {
  const { authState, onAuthEvent, onStateChange } = props;
  const { changeState } = notify(authState, onAuthEvent, onStateChange);

  return (
    <div className={styles.landingWrapperLoaded}>
      <title>App Shield Management</title>
      <LandingForm {...props} />
      <LandingFooter changeState={changeState} />
    </div>
  );
};

const LandingPageWrapper: React.FC<AuthPieceProps> = (props) => {
  if (props.authState && ["loading", "signedIn"].includes(props.authState)) {
    return null;
  }

  return (
    <BrowserRouter>
      <Switch>
        {process.env.REACT_APP_REDIRECT_TO_PLATFORM && (
          <Route
            path="/app-shield-management"
            exact
            render={() => <ManagementLandingPage {...props} />}
          />
        )}

        <Route
          path="/login"
          exact
          render={() => <LandingPage {...props} page="login" />}
        />

        <Route
          path="/terms"
          exact
          render={() => <LandingPage {...props} page="terms" />}
        />

        <Route path="/Logout" exact component={Logout} />

        <Route path="/" render={() => <LandingPage {...props} />} />
      </Switch>
    </BrowserRouter>
  );
};

export default LandingPageWrapper;
